/**
 * The breakdown as numbers - one row per subsystem, one column per quantity.
 *
 * Every chart on this page has a table twin, and this is all of them at once
 * because the rows are the same parts either way. It is not a fallback: a bar
 * length answers "where is the mass" and a column of figures answers "what
 * exactly", and a budget gets asked both. It is also the WCAG-clean copy - real
 * table semantics, every value as text, nothing behind a hover.
 *
 * The share column is here and NOT on the chart. Bar length already carries the
 * share visually; printing `60%` beside a total that is itself a floor over a
 * partial would give a ratio two caveats deep a precision it has not got. In a
 * table, with the caveat in the neighbouring cell, it reads as the arithmetic it
 * is.
 */

import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { MONOSPACE } from '../../theme'
import type { Breakdown } from './budget-model'
import { formatTotal } from './budget-model'

/**
 * A slice's share of the headline, as a percentage.
 *
 * Null whenever the ratio would be invented rather than computed: either end
 * unknown, or a headline of zero. A share of a total nobody stated is not 0% and
 * not 100%; it is a question the model has not answered.
 *
 * @param part the slice's total
 * @param whole the root's total
 */
export function shareOf(part: number | null, whole: number | null | undefined): string {
  if (part === null || whole === null || whole === undefined || whole <= 0) return '—'
  return `${((part / whole) * 100).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`
}

export default function BudgetTable({
  breakdowns,
  rootName,
}: {
  /** One per quantity, all over the same root and therefore the same rows. */
  breakdowns: Breakdown[]
  rootName: string
}) {
  const first = breakdowns[0]
  if (!first || first.slices.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {rootName} declares no parts inside it, so there is nothing to break down.
      </Typography>
    )
  }

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table aria-label={`Budget breakdown of ${rootName}`}>
        <TableHead>
          <TableRow>
            <TableCell>Part</TableCell>
            <TableCell>ID</TableCell>
            {breakdowns.map((breakdown) => (
              <TableCell key={breakdown.attribute.name} align="right">
                {breakdown.attribute.label}
              </TableCell>
            ))}
            <TableCell align="right">Share of {first.attribute.label.toLowerCase()}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/*
            Keyed and ordered off the FIRST quantity, and every other column is
            looked up by key rather than by position: the slices are sorted by
            magnitude, so power draw's order is not mass's, and zipping the lists
            by index would put the structure's mass on the guidance's row.
          */}
          {first.slices.map((slice) => (
            <TableRow key={slice.key} hover>
              <TableCell>{slice.label}</TableCell>
              <TableCell sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>
                {slice.id || '—'}
              </TableCell>
              {breakdowns.map((breakdown) => {
                const match = breakdown.slices.find((entry) => entry.key === slice.key)
                return (
                  <TableCell
                    key={breakdown.attribute.name}
                    align="right"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {match
                      ? formatTotal({
                          own: null,
                          total: match.total,
                          unit: match.unit,
                          is_partial: match.isPartial,
                          unstated: match.unstated,
                          how: match.how,
                          bound: match.bound,
                          problems: match.problems,
                        })
                      : '—'}
                  </TableCell>
                )
              })}
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {shareOf(slice.total, first.total?.total)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{rootName}, in total</TableCell>
            <TableCell />
            {breakdowns.map((breakdown) => (
              <TableCell
                key={breakdown.attribute.name}
                align="right"
                sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatTotal(breakdown.total)}
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}
